/* wrapd personalizer -- name input, debounced live preview, cart line properties.
 *
 * Two things this file must never do:
 *   1. Draw a preview itself. Every pixel comes from the wrapd service, which
 *      renders it with the same generator that makes the print file.
 *   2. Let a roll reach the cart without a name on it.
 *
 * Previews load as plain <img> src, so no CORS and no fetch -- the browser
 * caches them, and re-typing an earlier name is instant.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 500;
  var PROP_NAME = 'Name to print';
  var PROP_DESIGN = '_design_key';

  /* What the print fonts can draw: Latin letters (accents included), digits and
   * everyday punctuation. None of the fonts has emoji, and an unknown character
   * prints as an EMPTY BOX on every name down the roll, so it is stopped here,
   * before it can be bought. This list is a convenience, not the authority:
   * the service checks the real fonts and holds any order that slips past
   * (renderer.unprintable; tests/test_guards.py keeps the two in step). */
  var UNSAFE = null;
  try {
    UNSAFE = new RegExp(
      '[^\\p{Script=Latin}\\p{N} .,\'\u2019"\u201c\u201d&\\-\u2013!?()/+#@:;*]', 'gu');
  } catch (e) {
    UNSAFE = null;  // a browser too old for \p{...}: the service still holds it
  }

  function unprintable(name) {
    if (!UNSAFE) return [];
    var seen = {}, out = [];
    var hits = name.match(UNSAFE) || [];
    hits.forEach(function (ch) {
      // U+FE0F / U+200D are the invisible glue inside an emoji -- naming them
      // in the message would only add blank gaps.
      if (ch === '\uFE0F' || ch === '\u200D' || seen[ch]) return;
      seen[ch] = true;
      out.push(ch);
    });
    return out.length || !hits.length ? out : ['?'];
  }

  function init(root) {
    if (root.dataset.wrapdReady) return;
    root.dataset.wrapdReady = '1';

    /* Every listener below is registered against this controller, so if the
     * widget is ever re-initialised on the same element (theme editor reload,
     * a design swap) the previous set is torn down first. Without this you get
     * two live handlers holding two different designs, and whichever runs last
     * wins -- which looks like the preview randomly showing the wrong design. */
    if (root._wrapdAbort) root._wrapdAbort.abort();
    var ac = new AbortController();
    root._wrapdAbort = ac;
    var on = { signal: ac.signal };

    /* Read settings from the DOM at the moment they are USED, never cached at
     * init. The theme editor patches these attributes in place when a merchant
     * edits a setting, without re-running this script -- so a cached app URL
     * keeps calling the previous address and every preview fails while the
     * panel shows the new value. */
    function appUrl() {
      var u = (root.dataset.appUrl || '').trim().replace(/\/+$/, '');
      /* A value with no scheme would be resolved against the storefront, so
       * the request quietly goes to the shop's own domain and 404s. Refuse to
       * guess silently -- say what is wrong, and repair the obvious case. */
      if (u && !/^https?:\/\//i.test(u)) {
        u = 'https://' + u.replace(/^\/+/, '').replace(/^https?:\/?/i, '');
        console.warn('[wrapd] app URL had no valid scheme; using ' + u);
      }
      return u;
    }
    function design() { return root.dataset.design || ''; }

    var maxLength = parseInt(root.dataset.maxLength, 10) || 30;
    var required = root.dataset.required === 'true';

    var input = root.querySelector('[data-wrapd-input]');
    var count = root.querySelector('[data-wrapd-count]');
    var error = root.querySelector('[data-wrapd-error]');
    var empty = root.querySelector('[data-wrapd-empty]');
    var spinner = root.querySelector('[data-wrapd-spinner]');
    var tabs = root.querySelectorAll('[data-wrapd-view]');
    var panes = {
      full: root.querySelector('[data-wrapd-pane="full"]'),
      closeup: root.querySelector('[data-wrapd-pane="closeup"]')
    };
    var imgs = {
      full: root.querySelector('[data-wrapd-img="full"]'),
      closeup: root.querySelector('[data-wrapd-img="closeup"]')
    };

    var view = 'full';
    var currentName = '';
    var blockedMsg = '';  // set while the name holds something that cannot print
    // key the cache on design too -- switching design must not reuse the
    // previous design's picture for the same name
    var loaded = { full: '', closeup: '' };
    function cacheKey(name) {
      return design() + '|' + name + '|' +
             (view === 'closeup' ? closeupInches() : 'full');
    }
    var timer = null;
    // A debounce already in flight would otherwise fire after this widget was
    // replaced and repaint the pane with the previous design.
    ac.signal.addEventListener('abort', function () { clearTimeout(timer); });

    var form = findProductForm(root);
    var fields = form ? attachFields(form) : null;
    if (!form) {
      console.warn('[wrapd] no product form found -- line item properties will not be set');
    }

    var CSS_PX_PER_IN = 96;
    var CLOSEUP_MIN_IN = 2.0, CLOSEUP_MAX_IN = 8.0;

    /* How many inches of paper the true-scale view can show HERE.
     * Life size means 96 CSS px per printed inch, so the window has to be
     * sized to the space available -- otherwise it is either cropped (a
     * customer sees one enormous name and thinks that is the product) or
     * scaled down, which quietly makes 'actual print size' a lie. */
    function closeupInches() {
      var box = panes.closeup.parentNode || root;
      var avail = box.clientWidth || root.clientWidth || 0;
      var inches = avail / CSS_PX_PER_IN;
      if (!inches) return CLOSEUP_MAX_IN;
      return Math.max(CLOSEUP_MIN_IN, Math.min(CLOSEUP_MAX_IN, Math.floor(inches * 4) / 4));
    }

    function previewUrl(name, v) {
      var u = appUrl() + '/preview?design=' + encodeURIComponent(design()) +
              '&name=' + encodeURIComponent(name) + '&view=' + v;
      if (v === 'closeup') u += '&win=' + closeupInches();
      return u;
    }

    function showError(msg) {
      error.textContent = msg;
      error.hidden = !msg;
    }

    function setBusy(on) {
      spinner.hidden = !on;
    }

    /* Load the visible pane only. The close-up is a bigger render, so it waits
     * until someone actually asks for it. */
    function refresh() {
      var name = currentName;
      if (!name) {
        empty.hidden = false;
        panes.full.hidden = true;
        panes.closeup.hidden = true;
        setBusy(false);
        return;
      }
      empty.hidden = true;
      panes.full.hidden = view !== 'full';
      panes.closeup.hidden = view !== 'closeup';

      if (loaded[view] === cacheKey(name)) {
        setBusy(false);
        return;
      }
      setBusy(true);
      var img = imgs[view];
      if (view === 'closeup') {
        var inches = closeupInches();
        img.style.width = (inches * CSS_PX_PER_IN) + 'px';
        img.style.height = 'auto';
      }
      var wanted = name;
      var wantedKey = cacheKey(wanted);
      img.onload = function () {
        loaded[view] = wantedKey;
        if (wanted === currentName) setBusy(false);
      };
      img.onerror = function () {
        setBusy(false);
        showError('Could not draw that preview. Your name is still saved -- ' +
                  'try a different spelling, or add to cart and we will check it.');
      };
      img.src = previewUrl(name, view);
    }

    function onType() {
      var value = input.value.slice(0, maxLength);
      // maxlength only binds typed input -- autofill and some mobile keyboards
      // get past it. Write the clamp back so the box always shows exactly what
      // will be printed; silent truncation is how you ship the wrong roll.
      if (value !== input.value) input.value = value;
      count.textContent = String(value.length);
      showError('');

      // Properties update immediately; only the render is debounced.
      currentName = value.trim().replace(/\s+/g, ' ');
      // "e" + a combining accent and a single accented letter look identical;
      // only the second is in the fonts.
      if (currentName.normalize) currentName = currentName.normalize('NFC');
      if (fields) {
        fields.name.value = currentName;
        fields.design.value = design();
      }

      clearTimeout(timer);
      var bad = unprintable(currentName);
      if (bad.length) {
        blockedMsg = 'Emoji and symbols cannot be printed (' + bad.join(' ') +
                     '). Please remove them. Letters, numbers and ' +
                     'punctuation like & and \' are fine.';
        setBusy(false);
        showError(blockedMsg);
        return;  // no preview: it would only show the empty boxes
      }
      blockedMsg = '';
      if (!currentName) {
        refresh();
        return;
      }
      setBusy(true);
      timer = setTimeout(refresh, DEBOUNCE_MS);
    }

    input.addEventListener('input', onType, on);

    /* Re-fetch the close-up when the space changes -- rotating a phone or
     * dragging a window otherwise leaves it at the old, no-longer-true size. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (view !== 'closeup' || !currentName) return;
        loaded.closeup = '';
        refresh();
      }, 250);
    }, on);
    ac.signal.addEventListener('abort', function () { clearTimeout(resizeTimer); });

    Array.prototype.forEach.call(tabs, function (tab) {
      tab.addEventListener('click', function () {
        view = tab.dataset.wrapdView;
        Array.prototype.forEach.call(tabs, function (t) {
          t.classList.toggle('is-active', t === tab);
        });
        clearTimeout(timer);
        refresh();
      }, on);
    });

    if (form) {
      /* On WINDOW, in the capture phase, on purpose. Horizon routes every
       * submit through one capturing listener on `document` (component.js,
       * on:submit="/handleSubmit") that adds to the cart without looking at
       * defaultPrevented. A listener on the form itself runs after it, so the
       * message showed and the roll went into the cart anyway (found on the
       * live store, Sept 18 2026). Window-capture is the only place earlier. */
      window.addEventListener('submit', function (e) {
        if (e.target !== form) return;
        var msg = blockedMsg ||
                  (required && !currentName ? 'Please enter the name you want printed.' : '');
        if (msg) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError(msg);
          input.focus();
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, { capture: true, signal: ac.signal });
    }

    onType();  // sync the counter and hidden fields on load
  }

  /* The section can sit anywhere on the product template, so find the add-to-cart
   * form rather than assuming the section wraps it. */
  function findProductForm(root) {
    var scope = root.closest('.shopify-section') || document;
    return scope.querySelector('form[action*="/cart/add"]') ||
           document.querySelector('form[action*="/cart/add"]');
  }

  function attachFields(form) {
    return {
      name: hiddenInput(form, 'properties[' + PROP_NAME + ']'),
      design: hiddenInput(form, 'properties[' + PROP_DESIGN + ']')
    };
  }

  function hiddenInput(form, name) {
    var el = form.querySelector('input[name="' + CSS.escape(name) + '"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    return el;
  }

  function boot() {
    document.querySelectorAll('[data-wrapd]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-init when the theme editor re-renders the section.
  document.addEventListener('shopify:section:load', boot);
})();
